import * as THREE from 'three';
import { ClippingGroup } from 'three/webgpu';

export interface BatchInstance {
  batch: LayerBatch;
  id: number;
}

export interface ElementEntry {
  expressID: number;
  type: string;
  name: string;
  globalId: string;
  storeyIndex: number;
  storey: string;
  instances: BatchInstance[];
}

export interface LayerBatches {
  root: ClippingGroup;
  batches: LayerBatch[];
  elements: Map<number, ElementEntry>;
  storeys: string[];
}

export interface BatchOptions {
  keyOf(mesh: THREE.Mesh): string;
  // extra instance, vertex and index capacity as a fraction, so a rebatch can refill in place
  spare?: number;
}

interface Counts {
  instances: number;
  vertices: number;
  indices: number;
}

// Every mesh of one storey that shares a material preset, drawn in one call. Per instance it
// remembers the element it belongs to and the colour it came with; the layer's element index
// rides along so a hit resolves to its element without walking the scene.
export class LayerBatch extends THREE.BatchedMesh {
  readonly expressIDs: number[] = [];
  readonly baseColors: THREE.Vector4[] = [];
  readonly geometryIds: number[] = [];

  constructor(
    readonly key: string,
    readonly storey: string,
    readonly glass: boolean,
    readonly source: THREE.Material,
    readonly capacity: Counts,
    readonly elements: Map<number, ElementEntry>,
  ) {
    super(capacity.instances, capacity.vertices, capacity.indices, source);
  }

  // A batch split into an opaque and a translucent pass carries two materials; the hit test
  // only needs one.
  override raycast(raycaster: THREE.Raycaster, intersects: THREE.Intersection[]): void {
    const material = this.material as THREE.Material | THREE.Material[];
    if (Array.isArray(material)) this.material = material[0]!;
    super.raycast(raycaster, intersects);
    this.material = material as THREE.Material;
  }
}

interface Part {
  expressID: number;
  mesh: THREE.Mesh;
  geometry: THREE.BufferGeometry;
  matrix: THREE.Matrix4;
  color: THREE.Vector4;
}

interface StoreyInfo {
  name: string;
  elements: { expressID: number; type: string; name: string; globalId: string; parts: Part[] }[];
}

interface Extras {
  kind?: 'element' | 'storey';
  expressID?: number;
  globalId?: string;
  type?: string;
  name?: string;
  elevation?: number | null;
}

export function materialOf(mesh: THREE.Mesh): THREE.Material {
  return Array.isArray(mesh.material) ? mesh.material[0]! : mesh.material;
}

function colorOf(material: THREE.Material): THREE.Vector4 {
  const c = (material as THREE.MeshStandardMaterial).color ?? new THREE.Color(1, 1, 1);
  return new THREE.Vector4(c.r, c.g, c.b, material.transparent ? material.opacity : 1);
}

// The material preset the source put on the mesh; translucent parts always batch as glass.
export function presetKey(mesh: THREE.Mesh): string {
  if (colorOf(materialOf(mesh)).w < 0.999) return 'glass';
  return (mesh.userData as { preset?: string }).preset ?? 'generic';
}

export function buildBatches(tree: THREE.Object3D, opts: BatchOptions): LayerBatches {
  const layer: LayerBatches = {
    root: new ClippingGroup(),
    batches: [],
    elements: new Map(),
    storeys: [],
  };
  const storeys = collect(tree);
  layer.storeys.push(...storeys.map((s) => s.name));
  storeys.forEach((s, i) => fillStorey(layer, s, i, opts, new Map()));
  return layer;
}

// Refills only the storeys whose element set changed, reusing a batch in place when the new
// content fits its capacity.
export function rebatchStoreys(
  layer: LayerBatches,
  tree: THREE.Object3D,
  opts: BatchOptions,
): void {
  const storeys = collect(tree);
  const names = storeys.map((s) => s.name);
  const before = new Map<string, Set<number>>();
  for (const el of layer.elements.values()) {
    const ids = before.get(el.storey) ?? new Set<number>();
    ids.add(el.expressID);
    before.set(el.storey, ids);
  }
  for (const b of layer.batches) if (!before.has(b.storey)) before.set(b.storey, new Set());
  for (const name of before.keys()) if (!names.includes(name)) dropStorey(layer, name, []);
  layer.storeys.length = 0;
  layer.storeys.push(...names);

  storeys.forEach((s, i) => {
    const old = before.get(s.name) ?? new Set<number>();
    const same = old.size === s.elements.length && s.elements.every((e) => old.has(e.expressID));
    if (same) {
      for (const id of old) layer.elements.get(id)!.storeyIndex = i;
      return;
    }
    const reusable = dropStorey(layer, s.name, [...groupByKey(s, opts).keys()]);
    fillStorey(layer, s, i, opts, reusable);
  });
}

export function disposeBatch(batch: LayerBatch): void {
  batch.removeFromParent();
  batch.dispose();
}

// World-space box of an element, from its instances' geometry bounds and matrices.
export function elementBox(el: ElementEntry, target: THREE.Box3): THREE.Box3 {
  const box = new THREE.Box3();
  const matrix = new THREE.Matrix4();
  target.makeEmpty();
  for (const { batch, id } of el.instances) {
    batch.updateWorldMatrix(true, false);
    if (!batch.getBoundingBoxAt(batch.getGeometryIdAt(id), box)) continue;
    batch.getMatrixAt(id, matrix).premultiply(batch.matrixWorld);
    target.union(box.applyMatrix4(matrix));
  }
  return target;
}

// A standalone copy of one instance, parented to its batch so it sits exactly on top of it.
// For overlays that need a real mesh (outline selection, vertex colours); never hit-tested.
export function instanceMesh(inst: BatchInstance, material: THREE.Material): THREE.Mesh {
  const { batch, id } = inst;
  const range = batch.getGeometryRangeAt(batch.getGeometryIdAt(id))!;
  const src = batch.geometry;
  const geometry = new THREE.BufferGeometry();
  for (const name of ['position', 'normal']) {
    const attr = src.getAttribute(name) as THREE.BufferAttribute;
    const array = attr.array.slice(
      range.vertexStart * 3,
      (range.vertexStart + range.vertexCount) * 3,
    ) as Float32Array;
    geometry.setAttribute(name, new THREE.BufferAttribute(array, 3));
  }
  const index = src.getIndex()!;
  const indices = new Uint32Array(range.indexCount);
  for (let i = 0; i < range.indexCount; i++)
    indices[i] = index.getX(range.indexStart + i) - range.vertexStart;
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.applyMatrix4(batch.getMatrixAt(id, new THREE.Matrix4()));
  const mesh = new THREE.Mesh(geometry, material);
  mesh.raycast = noRaycast;
  batch.add(mesh);
  return mesh;
}

function noRaycast(): void {}

function dropStorey(layer: LayerBatches, storey: string, keep: string[]): Map<string, LayerBatch> {
  for (const [id, el] of layer.elements) if (el.storey === storey) layer.elements.delete(id);
  const reusable = new Map<string, LayerBatch>();
  layer.batches = layer.batches.filter((b) => {
    if (b.storey !== storey) return true;
    if (keep.includes(b.key)) {
      reusable.set(b.key, b);
      return true;
    }
    disposeBatch(b);
    return false;
  });
  return reusable;
}

function fillStorey(
  layer: LayerBatches,
  storey: StoreyInfo,
  storeyIndex: number,
  opts: BatchOptions,
  reusable: Map<string, LayerBatch>,
): void {
  for (const el of storey.elements) {
    layer.elements.set(el.expressID, {
      expressID: el.expressID,
      type: el.type,
      name: el.name,
      globalId: el.globalId,
      storeyIndex,
      storey: storey.name,
      instances: [],
    });
  }
  for (const [key, parts] of groupByKey(storey, opts)) {
    const need = countParts(parts);
    let batch = reusable.get(key);
    if (batch && fits(need, batch.capacity)) {
      clearBatch(batch);
    } else {
      if (batch) {
        layer.batches.splice(layer.batches.indexOf(batch), 1);
        disposeBatch(batch);
      }
      const spare = 1 + (opts.spare ?? 0);
      const capacity = {
        instances: Math.ceil(need.instances * spare),
        vertices: Math.ceil(need.vertices * spare),
        indices: Math.ceil(need.indices * spare),
      };
      const glass = parts[0]!.color.w < 1;
      const source = materialOf(parts[0]!.mesh);
      batch = new LayerBatch(key, storey.name, glass, source, capacity, layer.elements);
      batch.name = `${storey.name} ${key}`;
      layer.batches.push(batch);
      layer.root.add(batch);
    }
    fillBatch(batch, parts, layer.elements);
  }
}

function fillBatch(batch: LayerBatch, parts: Part[], elements: Map<number, ElementEntry>): void {
  const geometryIds = new Map<THREE.BufferGeometry, number>();
  for (const part of parts) {
    let geometryId = geometryIds.get(part.geometry);
    if (geometryId == null) {
      geometryId = batch.addGeometry(part.geometry);
      geometryIds.set(part.geometry, geometryId);
      batch.geometryIds.push(geometryId);
    }
    const id = batch.addInstance(geometryId);
    batch.setMatrixAt(id, part.matrix);
    batch.setColorAt(id, part.color);
    batch.expressIDs[id] = part.expressID;
    batch.baseColors[id] = part.color;
    elements.get(part.expressID)!.instances.push({ batch, id });
  }
  batch.computeBoundingBox();
  batch.computeBoundingSphere();
}

function clearBatch(batch: LayerBatch): void {
  for (const id of batch.geometryIds) batch.deleteGeometry(id);
  batch.optimize();
  batch.geometryIds.length = 0;
  batch.expressIDs.length = 0;
  batch.baseColors.length = 0;
}

function fits(need: Counts, capacity: Counts): boolean {
  return (
    need.instances <= capacity.instances &&
    need.vertices <= capacity.vertices &&
    need.indices <= capacity.indices
  );
}

function countParts(parts: Part[]): Counts {
  const seen = new Set<THREE.BufferGeometry>();
  const counts: Counts = { instances: parts.length, vertices: 0, indices: 0 };
  for (const { geometry } of parts) {
    if (seen.has(geometry)) continue;
    seen.add(geometry);
    counts.vertices += geometry.getAttribute('position').count;
    counts.indices += geometry.getIndex()!.count;
  }
  return counts;
}

function groupByKey(storey: StoreyInfo, opts: BatchOptions): Map<string, Part[]> {
  const groups = new Map<string, Part[]>();
  for (const el of storey.elements) {
    for (const part of el.parts) {
      const key = opts.keyOf(part.mesh);
      const list = groups.get(key);
      if (list) list.push(part);
      else groups.set(key, [part]);
    }
  }
  return groups;
}

// Walks the storey > element > mesh tree the sources produce; storeys bottom to top.
function collect(tree: THREE.Object3D): StoreyInfo[] {
  tree.updateMatrixWorld(true);
  const inverse = tree.matrixWorld.clone().invert();
  const prepared = new Map<THREE.BufferGeometry, THREE.BufferGeometry>();
  const storeyNodes: { node: THREE.Object3D; elevation: number }[] = [];
  tree.traverse((o) => {
    const x = o.userData as Extras;
    if (x.kind === 'storey') storeyNodes.push({ node: o, elevation: x.elevation ?? 0 });
  });
  storeyNodes.sort((a, b) => a.elevation - b.elevation);
  return storeyNodes.map(({ node }) => ({
    name: node.name,
    elements: node.children.flatMap((child) => {
      const x = child.userData as Extras;
      if (x.kind !== 'element' || x.expressID == null) return [];
      const expressID = x.expressID;
      const parts: Part[] = [];
      child.traverse((m) => {
        const mesh = m as THREE.Mesh;
        if (!mesh.isMesh) return;
        let geometry = prepared.get(mesh.geometry);
        if (!geometry) {
          geometry = prepare(mesh.geometry);
          prepared.set(mesh.geometry, geometry);
        }
        parts.push({
          expressID,
          mesh,
          geometry,
          matrix: mesh.matrixWorld.clone().premultiply(inverse),
          color: colorOf(materialOf(mesh)),
        });
      });
      return [
        {
          expressID,
          type: x.type ?? '',
          name: x.name ?? '',
          globalId: x.globalId ?? '',
          parts,
        },
      ];
    }),
  }));
}

// Batches need one attribute layout: float position and normal, 32-bit indices on input
// (the batch picks 16 or 32 bits for itself from its vertex count).
function prepare(src: THREE.BufferGeometry): THREE.BufferGeometry {
  const out = new THREE.BufferGeometry();
  const position = src.getAttribute('position');
  const normal = src.getAttribute('normal');
  const count = position.count;
  const copy = (attr: THREE.BufferAttribute | THREE.InterleavedBufferAttribute) => {
    const array = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      array[i * 3] = attr.getX(i);
      array[i * 3 + 1] = attr.getY(i);
      array[i * 3 + 2] = attr.getZ(i);
    }
    return new THREE.BufferAttribute(array, 3);
  };
  out.setAttribute('position', copy(position));
  const index = src.getIndex();
  const indices = new Uint32Array(index ? index.count : count);
  for (let i = 0; i < indices.length; i++) indices[i] = index ? index.getX(i) : i;
  out.setIndex(new THREE.BufferAttribute(indices, 1));
  if (normal) out.setAttribute('normal', copy(normal));
  else out.computeVertexNormals();
  return out;
}
